import './styles.css';

import React, { useCallback, useState } from 'react';
import { Modal, Select, Input, SuggestInput, Button, Link } from '@vtblife/uikit';

import classname from '@search/classname/src';
import { useClassifiedOfferDataLayerPush } from '@search/vtbeco-frontend-core/domain/offer/analytics/ClassifiedOfferGtmContext';
import { validateEmail } from '@search/vtbeco-frontend-core/domain/emails/validateEmail';
import { PersonalDataPolicy } from '@search/vtbeco-frontend-core/view/common/components/PersonalDataPolicy/PersonalDataPolicy';
import { AgentCustomersRadioList } from '../../../Agent/AgentCustomersRadioList/AgentCustomersRadioList';
import { useLkLinks } from '../../useLkLinks';
import { useEmailsState } from './useEmailsState';

type Props = {
    onClose: () => void;
    searchTitle: string;
    searchEmail: string;
    setSearchTitle: (value: string) => void;
    setSearchEmail: (value: string) => void;
    handleOnSubmit: () => void;
    confirmEmail: () => void;
    isMobile?: boolean;
    isProfessional?: boolean;
    handleOnSubmitWithCustomers: (p: { forMe: boolean; forCustomers: string[] }) => void;
}

const cn = classname.bind(null, 'SaveSearchModal');

export const SaveSearchModal = ({
    setSearchTitle,
    setSearchEmail,
    handleOnSubmit,
    searchEmail,
    searchTitle,
    confirmEmail,
    onClose,
    isMobile,
    isProfessional,
    handleOnSubmitWithCustomers,
}: Props) => {
    const {
        emailsSuggestions,
        selectedEmail,
        setEmail,
        isNewEmail,
        setEmailOption
    } = useEmailsState(searchEmail, setSearchEmail);

    const [ forMe, setForMe ] = useState(true);
    const [ selectedCustomerIds, setSelectedCustomerIds ] = useState<Array<string>>([]);
    const handleAddSelectedCustomerId = useCallback((customerId: string) => {
        setSelectedCustomerIds((prevS) => {
            let newS = [ ...prevS ];

            if (! prevS.includes(customerId)) {
                newS.push(customerId);
            } else {
                newS = newS.filter(c => c !== customerId);
            }

            return newS;
        });
    }, [ ]);

    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const onTitleChange = React.useCallback((value: string) => {
        setSearchTitle(value);
    }, [ setSearchTitle ]);

    const isValidEmail = React.useMemo(() => validateEmail(searchEmail), [ searchEmail ]);

    const isDisabledButton = React.useMemo(() => {
        if (! searchTitle) return true;

        if (isProfessional) {
            if (! forMe && ! selectedCustomerIds.length) return true;
        }

        if (searchEmail) {
            return ! isValidEmail;
        }

        return false;
    }, [ searchTitle, searchEmail, isProfessional, isValidEmail, forMe, selectedCustomerIds.length ]);

    React.useEffect(() => {
        dataLayerPush({ event: 'classified_show_card_save_search' });
    }, [ dataLayerPush ]);

    const onSave = React.useCallback(() => {
        if (searchEmail && isNewEmail) {
            confirmEmail();
        } else {
            if (! isProfessional) {
                handleOnSubmit();
            } else {
                handleOnSubmitWithCustomers({
                    forMe,
                    forCustomers: selectedCustomerIds
                });
            }
        }
        dataLayerPush({ event: 'classified_card_click_save_search' });
    }, [
        searchEmail, isNewEmail, dataLayerPush, confirmEmail, handleOnSubmit,
        isProfessional, handleOnSubmitWithCustomers, forMe, selectedCustomerIds
    ]);

    const { savedSearchUrl } = useLkLinks();

    return (
        <Modal size='s' opened onClose={onClose}>
            <div className={cn('title')}>
                <Modal.Title>Сохранить поиск</Modal.Title>
            </div>
            <p className={cn('description')}>
                Все сохраненные поиски находятся <br /> в&nbsp;разделе&nbsp;
                <Link variant='primary-minor' href={savedSearchUrl} target='blank'>Подписки</Link> в личном кабинете
            </p>
            <div className={cn('title-input')}>
                <Input
                    label='Название поиска'
                    placeholder='Название поиска'
                    size='l'
                    value={searchTitle}
                    onChange={onTitleChange}
                />
            </div>
            <Select mode='single' onChange={setEmailOption}>
                <div className={cn('email-input')}>
                    <SuggestInput
                        label='Получать на почту'
                        onChange={setEmail}
                        valid={isValidEmail}
                        value={selectedEmail}
                        debounce={100}
                        size='l'
                    />
                </div>
                {emailsSuggestions.length > 0 && (
                    <Select.Content adaptivePlacement={false} height='200px'>
                        {emailsSuggestions.map(data => (
                            <Select.Option value={data.value} key={data.label}>
                                {data.value}
                            </Select.Option>
                        ))}
                    </Select.Content>
                )}
            </Select>
            {isProfessional ? (
                <div className={cn('customers-list')}>
                    <AgentCustomersRadioList
                        forMe={forMe}
                        onForMe={(v) => setForMe(v)}
                        selectedIds={selectedCustomerIds}
                        onSelect={handleAddSelectedCustomerId}
                    />
                </div>
            ) : null}
            <Modal.Actions>
                <Button
                    dataTest='save-search-modal-send'
                    onClick={onSave}
                    disabled={isDisabledButton}
                >
                    Сохранить
                </Button>
            </Modal.Actions>
            <PersonalDataPolicy isMobile={isMobile} actionTitle='Сохранить' />
        </Modal>
    );
};
